import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Users } from "lucide-react";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/Input";
import { Select } from "../../ui/Select";
import { cn } from "../../../utils/cn";
import {
  getAnnouncementAudienceOptions,
  previewAnnouncementAudience,
} from "../../../api/announcements";
import CommunePicker from "./CommunePicker";
import { audiencePayload, ownerParams } from "./announcementForm";

// ─────────────────────────────────────────────────────────────────────────────
// A quién le llega el pop-up: todos, o los comercios que cumplan integración
// y/o comuna (Y u O, lo elige el autor). El alcance real lo calcula el
// servidor con los datos de hoy ("Calcular alcance").
// ─────────────────────────────────────────────────────────────────────────────

// Países del catálogo de comunas (para el superadmin del sistema, que no tiene
// país propio). El de un operador sale de su sesión.
const CATALOG_COUNTRIES = [
  { value: "CL", label: "Chile" },
  { value: "AR", label: "Argentina" },
  { value: "CO", label: "Colombia" },
  { value: "PE", label: "Perú" },
];

function Segmented({ value, onChange, options, ariaLabel }) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} className="inline-flex rounded-lg border border-border p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
            value === option.value ? "bg-brand-500 text-white" : "text-fg-muted hover:bg-bg-muted",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function SectionTitle({ children }) {
  return <h4 className="text-sm font-semibold text-fg">{children}</h4>;
}

/**
 * @param {{
 *   form: object,
 *   onAudienceChange: (patch: object) => void,
 *   systemMode: boolean,
 *   operators: {id: string, name: string}[],
 *   operatorCountryCode: string,
 *   error?: string,
 * }} props
 */
export default function AudienceEditor({
  form,
  onAudienceChange,
  systemMode,
  operators,
  operatorCountryCode,
  error,
}) {
  const { t } = useTranslation();
  const { audience } = form;
  const filtered = audience.mode === "filtered";
  const isPlatform = form.scope === "platform";
  const params = useMemo(
    () => ownerParams(form, { systemMode }),
    // Solo el dueño importa para las opciones.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [systemMode, form.scope, form.logisticsCompanyId],
  );
  const ownerReady = !systemMode || isPlatform || Boolean(form.logisticsCompanyId);

  const [countryCode, setCountryCode] = useState(operatorCountryCode || "CL");
  const [options, setOptions] = useState([]);
  const [optionsState, setOptionsState] = useState({ loading: false, error: "" });
  const [operatorQuery, setOperatorQuery] = useState("");
  const [reach, setReach] = useState(null);
  const [reachState, setReachState] = useState({ loading: false, error: "" });
  const [showSample, setShowSample] = useState(false);

  useEffect(() => {
    if (!systemMode && operatorCountryCode) setCountryCode(operatorCountryCode);
  }, [systemMode, operatorCountryCode]);

  // Integraciones reales del universo del dueño.
  useEffect(() => {
    if (!filtered || !ownerReady) return undefined;
    let cancelled = false;
    setOptionsState({ loading: true, error: "" });
    (async () => {
      const result = await getAnnouncementAudienceOptions(params);
      if (cancelled) return;
      setOptionsState({ loading: false, error: result.ok ? "" : t(result.error) });
      setOptions(result.ok ? result.data?.integrations ?? [] : []);
    })();
    return () => {
      cancelled = true;
    };
  }, [filtered, ownerReady, params, t]);

  // El alcance calculado deja de valer apenas cambia la audiencia o el dueño.
  const audienceKey = JSON.stringify({ a: audiencePayload(form), o: params });
  const [reachKey, setReachKey] = useState(null);
  const reachIsStale = reach !== null && reachKey !== audienceKey;

  const calculateReach = async () => {
    setReachState({ loading: true, error: "" });
    const result = await previewAnnouncementAudience({ ...params, audience: audiencePayload(form) });
    setReachState({ loading: false, error: result.ok ? "" : t(result.error) });
    setReach(result.ok ? result.data : null);
    setReachKey(audienceKey);
    setShowSample(false);
  };

  // Lo elegido que ya no aparece entre las opciones (p. ej. al editar) sigue visible.
  const integrationRows = useMemo(() => {
    const rows = options.map((o) => ({ label: o.label, companies: o.companies }));
    for (const label of audience.integrations) {
      if (!rows.some((r) => r.label === label)) rows.push({ label, companies: null });
    }
    return rows;
  }, [options, audience.integrations]);

  const toggleIntegration = (label) => {
    const has = audience.integrations.includes(label);
    onAudienceChange({
      integrations: has
        ? audience.integrations.filter((l) => l !== label)
        : [...audience.integrations, label],
    });
  };

  const toggleOperator = (id) => {
    const has = audience.operatorIds.includes(id);
    onAudienceChange({
      operatorIds: has ? audience.operatorIds.filter((o) => o !== id) : [...audience.operatorIds, id],
    });
  };

  const visibleOperators = operators.filter((o) =>
    o.name.toLowerCase().includes(operatorQuery.trim().toLowerCase()),
  );

  return (
    <div className="flex flex-col gap-4">
      <Segmented
        ariaLabel={t("A quién se muestra")}
        value={audience.mode}
        onChange={(mode) => onAudienceChange({ mode })}
        options={[
          {
            value: "all",
            label: isPlatform ? t("Todos los comercios") : t("Todos mis comercios"),
          },
          { value: "filtered", label: t("Solo algunos comercios") },
        ]}
      />

      {systemMode && isPlatform && (
        <div className="flex flex-col gap-2">
          <SectionTitle>{t("Operadores")}</SectionTitle>
          <p className="text-xs text-fg-muted">
            {audience.operatorIds.length === 0
              ? t("Sin elegir: llega a los comercios de todos los operadores.")
              : t("Solo comercios de {{n}} operadores elegidos.", { n: audience.operatorIds.length })}
          </p>
          <Input
            size="sm"
            value={operatorQuery}
            onChange={(e) => setOperatorQuery(e.target.value)}
            placeholder={t("Buscar operador")}
            aria-label={t("Buscar operador")}
          />
          <ul className="max-h-40 overflow-y-auto rounded-lg border border-border p-2">
            {visibleOperators.map((operator) => (
              <li key={operator.id}>
                <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-fg hover:bg-bg-muted">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-brand-500"
                    checked={audience.operatorIds.includes(operator.id)}
                    onChange={() => toggleOperator(operator.id)}
                  />
                  {operator.name}
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}

      {filtered && (
        <>
          <div className="flex flex-col gap-2">
            <SectionTitle>{t("Integración")}</SectionTitle>
            <p className="text-xs text-fg-muted">
              {t("Comercios que tengan al menos una de las integraciones elegidas.")}
            </p>
            {!ownerReady ? (
              <p className="text-sm text-fg-muted">{t("Elige el operador para ver sus integraciones.")}</p>
            ) : optionsState.loading ? (
              <p className="text-sm text-fg-muted">{t("Cargando integraciones…")}</p>
            ) : optionsState.error ? (
              <p className="text-sm text-status-error">{optionsState.error}</p>
            ) : integrationRows.length === 0 ? (
              <p className="text-sm text-fg-muted">{t("Ningún comercio tiene integraciones todavía.")}</p>
            ) : (
              <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                {integrationRows.map((row) => (
                  <li key={row.label}>
                    <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-fg hover:bg-bg-muted">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-brand-500"
                        checked={audience.integrations.includes(row.label)}
                        onChange={() => toggleIntegration(row.label)}
                      />
                      <span className="flex-1">{row.label}</span>
                      {row.companies !== null && (
                        <span className="text-xs tabular-nums text-fg-muted">
                          {t("{{n}} comercios", { n: row.companies })}
                        </span>
                      )}
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <SectionTitle>{t("Comuna")}</SectionTitle>
              {systemMode && (
                <div className="w-40">
                  <Select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    options={CATALOG_COUNTRIES.map((c) => ({ value: c.value, label: t(c.label) }))}
                    aria-label={t("País")}
                  />
                </div>
              )}
            </div>
            <p className="text-xs text-fg-muted">
              {t("Comercios con al menos una dirección de retiro en las comunas elegidas.")}
            </p>
            <CommunePicker
              countryCode={countryCode}
              selected={audience.communes}
              onChange={(communes) => onAudienceChange({ communes })}
            />
          </div>

          {audience.integrations.length > 0 && audience.communes.length > 0 && (
            <div className="flex flex-col gap-2">
              <SectionTitle>{t("Cómo combinar los criterios")}</SectionTitle>
              <Segmented
                ariaLabel={t("Cómo combinar los criterios")}
                value={audience.match}
                onChange={(match) => onAudienceChange({ match })}
                options={[
                  { value: "all", label: t("Integración Y comuna") },
                  { value: "any", label: t("Integración O comuna") },
                ]}
              />
              <p className="text-xs text-fg-muted">
                {audience.match === "all"
                  ? t("Debe cumplir los dos criterios.")
                  : t("Alcanza con cumplir uno de los dos.")}
              </p>
            </div>
          )}
        </>
      )}

      {error && <p className="text-sm text-status-error">{t(error)}</p>}

      <div className="flex flex-col gap-2 rounded-lg border border-border bg-bg-muted p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm text-fg">
            <Users className="h-4 w-4 text-fg-muted" aria-hidden="true" />
            {reach && !reachIsStale ? (
              <span>
                {t("Hoy llega a {{n}} comercios", { n: reach.total })}
              </span>
            ) : (
              <span className="text-fg-muted">
                {reachIsStale ? t("La audiencia cambió: vuelve a calcular.") : t("Calcula a cuántos comercios llega hoy.")}
              </span>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={calculateReach}
            loading={reachState.loading}
            disabled={!ownerReady}
          >
            {t("Calcular alcance")}
          </Button>
        </div>
        {reachState.error && <p className="text-xs text-status-error">{reachState.error}</p>}
        {reach && !reachIsStale && reach.sample?.length > 0 && (
          <div>
            <button
              type="button"
              className="text-xs font-medium text-brand-600 hover:underline"
              onClick={() => setShowSample((v) => !v)}
              aria-expanded={showSample}
            >
              {showSample ? t("Ocultar comercios") : t("Ver comercios")}
            </button>
            {showSample && (
              <ul className="mt-2 max-h-40 overflow-y-auto text-xs text-fg-muted">
                {reach.sample.map((commerce) => (
                  <li key={commerce.companyId}>{commerce.name || commerce.companyId}</li>
                ))}
                {reach.total > reach.sample.length && (
                  <li className="mt-1 italic">
                    {t("y {{n}} más", { n: reach.total - reach.sample.length })}
                  </li>
                )}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
